import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import { NGXLogger } from 'ngx-logger';
import { Preferences } from '@capacitor/preferences';
import { ConfigurationService } from '@nsk-app/services/configuration.service';
import { AnalyticsEvent, EventType } from '@nsk-app/models/analyticsEvent';
import { ApiService } from '@nsk-app/services/api.service';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private trackingId: string;
  private eventDatabase = null;

  constructor(
    private logger: NGXLogger,
    private api: ApiService,
    private configuration: ConfigurationService
  ) { }

  public async initService() {
    this.eventDatabase = await new Dexie("analytics");
    await this.eventDatabase.version(1).stores({
      analyticsEvents: "++id, [event+date+linkedObject], count"
    });

    await this.logEvent(EventType.AppStart, this.configuration.topic.id);

    await this.updateTracking();
  }

  public async updateTracking(): Promise<boolean> {
    if(Capacitor.getPlatform() === 'web'){
      return false;
    }


    const id = await Preferences.get({key: 'trackingId'});
    const pushId = await Preferences.get({key: 'push_token'});
    if(!id.value){
      try{
        const res = await this.api.registerApp(this.configuration.topic.id,this.configuration.pushEnabled,pushId.value);
        await Preferences.set({key: 'trackingId',value: res.tracking_id});
        this.trackingId = res.tracking_id;

        await this.logEvent(EventType.Installation);
      }catch (e){
        this.logger.fatal('Could not register device!',e);
        this.trackingId = null;
        return false;
      }

    }else{
      this.trackingId = id.value;
      try{
        await this.api.registerApp(this.configuration.topic.id,this.configuration.pushEnabled,pushId.value, this.trackingId);
      }catch (e){
        return false;
      }
    }

    return true;
  }

  public async submitEvents() {
    if(this.eventDatabase == null){
      try{
        await this.initService();
      }catch (e){
        this.logger.fatal('Could not initialize.',e);
        return;
      }
    }else if(!this.trackingId){
      this.logger.fatal("No tracking id found");
    }

    try{
      const events: AnalyticsEvent[] = await this.eventDatabase.analyticsEvents.toArray();
      await this.api.submitAnalytics(this.trackingId, events);

      this.logger.debug('Submitted events',events);
      await this.eventDatabase.analyticsEvents.clear();

    }catch (e){
      this.logger.error('Could not submit analytics events.',e);
    }
  }

  public async logEvent(event: EventType, linkedObject: number = -1){
    if(this.eventDatabase == null){
      try{
        await this.initService();
      }catch (e){
        this.logger.fatal('Could not initialize.',e);
        return;
      }
    }

    const date = (new Date().toJSON()).split('T')[0];

    try{
      const e = await this.eventDatabase.analyticsEvents.where({event, date, linkedObject}).first();
      ++e.count;
      await this.eventDatabase.analyticsEvents.put(e);
    }catch (e){
      this.logger.debug("Could not update event, maybe it doesn't exist?");

      await this.eventDatabase.analyticsEvents.add({
        event,
        date,
        linkedObject,
        count: 1
      });
    }
  }
}
